import {
  AudioVideoObserver,
  ConsoleLogger,
  ContentShareObserver,
  DefaultBrowserBehavior,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  VideoSource,
  VideoTileState,
} from 'amazon-chime-sdk-js';
import { MediaSettings } from '../../contexts/videoCall/types';
import { logErrorAndReport } from '../../lib/errorReporting';
import MixpanelClient, { TrackingEventV2 } from '../../lib/tracking/mixpanel';
import { IOVideoSetupResult, Participant } from './meeting-call.types';

export type ObserverHandler = {
  localParticipantUpdated(participant: Participant): void;
  remoteParticipantsUpdated(participants: Participant[]): void;
};

export class VideoController implements AudioVideoObserver, ContentShareObserver {
  readonly logger: ConsoleLogger;
  readonly deviceController: DefaultDeviceController;
  private session: DefaultMeetingSession | null = null;
  private meeting: unknown | null = null;
  private attendee: { AttendeeId: string } | null = null;
  private remoteParticipants: Participant[] = [];
  private customObserver: ObserverHandler;

  private subscribedVolumeIds = new Set<string>();
  private lastMuted = new Map<string, boolean>();
  private lastVideoActive = new Map<string, boolean>();
  private presenceSubscribed = false;
  private hasTrackedLocalConnected = false;

  constructor(meeting: unknown, attendee: { AttendeeId: string }, observer: ObserverHandler) {
    this.logger = new ConsoleLogger('EnaraLogger', LogLevel.ERROR);
    this.deviceController = new DefaultDeviceController(this.logger);
    this.session = null;
    this.meeting = meeting;
    this.attendee = attendee;
    this.customObserver = observer;
  }

  private volumeIndicatorHandler = (
    id: string,
    _volume?: number | null,
    muted?: boolean | null,
    _signalStrength?: number | null
  ): void => {
    if (muted === undefined || muted === null) {
      return;
    }

    const isMuted = Boolean(muted);
    const prev = this.lastMuted.get(id);

    if (prev === undefined || prev !== isMuted) {
      MixpanelClient.trackEvent({
        eventName: isMuted
          ? TrackingEventV2.VideoCallLocalParticipantHasDisabledAudio
          : TrackingEventV2.VideoCallLocalParticipantHasEnabledAudio,
        properties: { attendeeId: id },
      });

      this.lastMuted.set(id, isMuted);
    }
  };

  public async startSetup(initialMediaSettings: MediaSettings): Promise<IOVideoSetupResult> {
    try {
      this.createConfiguration();
      const setupResult = await this.setupIO('default', 'default', initialMediaSettings);
      if (!setupResult.success) {
        return setupResult;
      }
    } catch (error) {
      logErrorAndReport({ error: `[VideoController::startSetup] error: ${error}` });
      return { success: false, message: `Problems configuring the audio and video` };
    }

    if (!this.session) {
      return { success: false, session: this.session };
    }

    const { audioVideo } = this.session;

    audioVideo.addObserver(this);
    audioVideo.addContentShareObserver(this);

    if (this.presenceSubscribed) {
      // Tray to avoid subscription of the handler in the same session.
      return { success: true, session: this.session };
    }

    this.presenceSubscribed = true;
    audioVideo.realtimeSubscribeToAttendeeIdPresence(
      (attendeeId, present, externalUserId, dropped) => {
        if (this.attendee?.AttendeeId !== attendeeId) {
          // other participants update

          if (present && !dropped) {
            this.upsertRemoteParticipant(attendeeId, externalUserId);
            this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
          } else {
            this.removeRemoteParticipant(attendeeId);
          }

          return;
        }

        // Local participant

        if (present && !dropped && !this.hasTrackedLocalConnected) {
          // local participant  subscribe just one time. Chime connection

          this.hasTrackedLocalConnected = true;
          MixpanelClient.trackEvent({
            eventName: TrackingEventV2.VideoCallLocalParticipantHasConnected,
            properties: { attendeeId },
          });
        }

        // subscribing volumeIndicator just one time per participant
        if (present && !dropped && !this.subscribedVolumeIds.has(attendeeId)) {
          this.session?.audioVideo.realtimeSubscribeToVolumeIndicator(
            attendeeId,
            this.volumeIndicatorHandler
          );

          this.subscribedVolumeIds.add(attendeeId);
        }

        if ((!present || dropped) && this.subscribedVolumeIds.has(attendeeId)) {
          //cleaning if fails

          this.session?.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId);
          this.subscribedVolumeIds.delete(attendeeId);
          this.lastMuted.delete(attendeeId);
        }
      }
    );

    return { success: true, session: this.session };
  }

  private removeRemoteParticipant(attendeeId: string) {
    this.remoteParticipants = this.remoteParticipants.filter(
      (participant) => participant.tileState.boundAttendeeId !== attendeeId
    );

    this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
  }

  private createConfiguration() {
    if (!this.meeting || !this.attendee) {
      return;
    }

    const configuration = new MeetingSessionConfiguration(this.meeting, this.attendee);
    this.session = new DefaultMeetingSession(configuration, this.logger, this.deviceController);
  }

  private async setupIO(
    audioId: string,
    videoId: string,
    initialMediaSettings: MediaSettings
  ): Promise<IOVideoSetupResult> {
    if (!audioId || !videoId || !this.session) {
      return { success: false, message: 'Problems to configure the audio and video' };
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    } catch (err) {
      logErrorAndReport({
        error: `browser doesn't have permissions enabled for microphone and camera ${
          (this.meeting as any).MeetingId
        }`,
      });
      return {
        success: false,
        message:
          'Please enable access to your microphone and camera in your browser settings to continue.',
      };
    }

    // Audio input
    if (audioId) {
      const audioInputDevices = await this.session.audioVideo.listAudioInputDevices();
      if (audioInputDevices.length > 0) {
        const found = audioInputDevices.find((audio) => {
          return audio.deviceId === audioId;
        });

        const audioDeviceId = found ? audioId : audioInputDevices[0].deviceId;
        await this.session.audioVideo.startAudioInput(audioDeviceId);
      }
    }

    // Audio output
    const audioOutputList = await this.session.audioVideo.listAudioOutputDevices();
    try {
      const defaultBrowserBehavior = new DefaultBrowserBehavior();
      if (defaultBrowserBehavior.supportsSetSinkId()) {
        if (audioOutputList.length > 0) {
          await this.session.audioVideo.chooseAudioOutput(audioOutputList[0].deviceId);
        } else {
          await this.session.audioVideo.chooseAudioOutput(null);
        }
      }
    } catch (err) {
      // Gracefully handle NotAllowedError when user gesture is required
      // This can happen when browser blocks setSinkId without user interaction
      this.logger.error(`Failed to set audio output device: ${err}`);
      // Continue setup instead of failing - audio will use default output
    }

    // Video input
    if (videoId) {
      const videoInputDevices = await this.session.audioVideo.listVideoInputDevices();
      await this.session.audioVideo.stopVideoInput();

      if (videoInputDevices.length > 0) {
        const defaultVideoId = videoInputDevices[0].deviceId;
        await this.session.audioVideo.startVideoInput(
          videoId === 'default' ? defaultVideoId : videoId
        );
      }
    }

    if (initialMediaSettings.micEnabled) {
      this.session.audioVideo.realtimeUnmuteLocalAudio();
      this.lastMuted.set(this.attendee!.AttendeeId, false);
    } else {
      this.session.audioVideo.realtimeMuteLocalAudio();
      this.lastMuted.set(this.attendee!.AttendeeId, true);
    }

    if (initialMediaSettings.videoEnabled) {
      await this.startVideo();
    } else {
      await this.stopVideo();
    }

    return { success: true };
  }

  private cleanupSubscriptions() {
    if (!this.session) {
      return;
    }

    for (const attendeeId of this.subscribedVolumeIds.keys()) {
      this.session.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId);
    }

    this.subscribedVolumeIds.clear();
    this.lastMuted.clear();
    this.lastVideoActive.clear();

    this.presenceSubscribed = false;
    this.hasTrackedLocalConnected = false;
  }

  public async unbindVideoIO() {
    this.cleanupSubscriptions();

    await this.session!.audioVideo.stopVideoInput();
    await this.session!.audioVideo.stopAudioInput();
    this.session!.audioVideo.stop();
  }

  public async stopVideo() {
    this.session!.audioVideo.stopLocalVideoTile();
    await this.session!.audioVideo.stopVideoInput();

    if (this.attendee?.AttendeeId) {
      this.lastVideoActive.set(this.attendee.AttendeeId, false);
    }
  }

  public async startVideo() {
    const deviceId = (await this.session!.audioVideo.listVideoInputDevices())[0].deviceId;
    await this.session!.audioVideo.startVideoInput(deviceId);
    this.session!.audioVideo.startLocalVideoTile();

    if (this.attendee?.AttendeeId) {
      // edge detection
      this.lastVideoActive.set(this.attendee.AttendeeId, true);
    }
  }

  private localTileUpdateObserver(tileState: VideoTileState) {
    if (!tileState.boundAttendeeId || !tileState.localTile) {
      return;
    }

    this.customObserver.localParticipantUpdated({
      isCameraEnabled: !!tileState?.boundVideoStream?.active || !!tileState.active,
      isRemote: false,
      tileState,
    });
  }

  private remoteTileUpdateObserver(tileState: VideoTileState) {
    if (!tileState.boundAttendeeId || tileState.localTile || tileState.isContent) {
      return;
    }

    const participant = this.upsertRemoteParticipant(
      tileState.boundAttendeeId,
      tileState.boundExternalUserId
    );

    participant.tileState = tileState;
    participant.isCameraEnabled = !!tileState.active || !!tileState?.boundVideoStream?.active;
    participant.isSharing = !!tileState.isContent;

    // binding only if area a video assigned and an active title
    const el = participant.boundElement?.current as HTMLVideoElement | undefined;
    if (el && tileState.tileId !== null && tileState.active) {
      this.session!.audioVideo.bindVideoElement(tileState.tileId, el);
    }

    this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
  }

  private sharingTileUpdateObserver(tileState: VideoTileState) {
    if (!tileState.boundAttendeeId || tileState.localTile || !tileState.isContent) {
      return;
    }

    const participant = this.upsertRemoteParticipant(
      tileState.boundAttendeeId,
      tileState.boundExternalUserId
    );

    participant.tileState = tileState;
    participant.isSharing = true;
    participant.isCameraEnabled = !!tileState.active;

    const el = participant.boundElement?.current as HTMLVideoElement | undefined;
    if (el && tileState.tileId !== null && tileState.active) {
      this.session!.audioVideo.bindVideoElement(tileState.tileId, el);
    }

    this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
  }

  audioVideoDidStart(): void {
    this.session!.audioVideo.startLocalVideoTile();
  }

  videoTileDidUpdate(tileState: VideoTileState) {
    this.localTileUpdateObserver(tileState);
    this.remoteTileUpdateObserver(tileState);
    this.sharingTileUpdateObserver(tileState);

    const attendeeId = tileState.boundAttendeeId;
    if (!attendeeId) {
      return;
    }

    if (this.attendee?.AttendeeId !== attendeeId) {
      return;
    }

    const nowActive = !!(tileState.active || tileState?.boundVideoStream?.active);
    const prevActive = this.lastVideoActive.get(attendeeId);

    if (prevActive === undefined || prevActive !== nowActive) {
      MixpanelClient.trackEvent({
        eventName: nowActive
          ? TrackingEventV2.VideoCallLocalParticipantHasEnabledVideo
          : TrackingEventV2.VideoCallLocalParticipantHasDisabledVideo,
        properties: { attendeeId },
      });

      this.lastVideoActive.set(attendeeId, nowActive);
    }
  }

  remoteVideoSourcesDidChange(videoSources: VideoSource[]) {
    this.remoteParticipants = this.remoteParticipants.map((rp) => {
      const idx = videoSources.findIndex(
        (vs) => vs.attendee.attendeeId === rp.tileState.boundAttendeeId
      );

      return { ...rp, isCameraEnabled: idx !== -1 ? true : false };
    });

    this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
  }

  videoTileWasRemoved(tileId: number) {
    const participant = this.remoteParticipants.find((x) => {
      return x.tileState?.tileId === tileId;
    });

    if (!participant) {
      return;
    }

    this.session!.audioVideo.unbindVideoElement(tileId);

    participant.isCameraEnabled = false;
    if (participant.tileState) {
      participant.tileState.active = false;
      participant.tileState.tileId = null;
    }

    this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
  }

  contentShareDidStop() {
    const sharingIndex = this.remoteParticipants.findIndex((p) => p.isSharing);
    if (sharingIndex !== -1) {
      const found = this.remoteParticipants[sharingIndex];
      if (found.tileState.tileId !== null) {
        this.session!.audioVideo.unbindVideoElement(found.tileState.tileId);
      }

      found.isSharing = false;
      this.customObserver.remoteParticipantsUpdated(this.remoteParticipants);
    }
  }

  private deriveName = (externalUserId?: string | null): string => {
    const base = externalUserId ?? '';
    const first = base.split('#')[0]?.trim();
    return first && first.length ? first : 'Guest';
  };

  private buildPlaceHolderNoVideoTile = (
    attendeeId: string,
    externalUserId?: string | null
  ): VideoTileState => {
    return {
      tileId: null,
      localTile: false,
      isContent: false,
      active: false,
      paused: false,
      poorConnection: false,
      boundAttendeeId: attendeeId,
      boundExternalUserId: externalUserId ?? null,
    } as unknown as VideoTileState;
  };

  private upsertRemoteParticipant = (attendeeId: string, externalUserId?: string | null) => {
    let participant = this.remoteParticipants.find(
      (p) => p.tileState?.boundAttendeeId === attendeeId
    );

    if (!participant) {
      participant = {
        isRemote: true,
        isCameraEnabled: false,
        isSharing: false,
        name: this.deriveName(externalUserId),
        tileState: this.buildPlaceHolderNoVideoTile(attendeeId, externalUserId ?? null),
        boundElement: undefined,
      } as Participant;

      this.remoteParticipants.push(participant);
    } else {
      if (!participant.name || participant.name.length === 0) {
        participant.name = this.deriveName(
          externalUserId ?? participant.tileState?.boundExternalUserId ?? null
        );
      }

      if (!participant.tileState)
        participant.tileState = this.buildPlaceHolderNoVideoTile(
          attendeeId,
          externalUserId ?? null
        );

      participant.tileState.boundAttendeeId = attendeeId;
      participant.tileState.boundExternalUserId =
        participant.tileState.boundExternalUserId ?? externalUserId ?? null;
    }

    return participant;
  };
}
